import React from "react";
import {
  Box,
  Menu,
  MenuItem,
  Typography,
  Badge,
  IconButton,
  Tooltip,
  List,
  ListItem,
  ListItemText,
  Divider,
  Button,
  Avatar,
  ListItemAvatar,
  Switch,
  FormControlLabel,
} from "@mui/material";
import {
  Bell,
  CircleDollarSign,
  ShoppingBag,
  Package,
  Users,
  FileQuestion,
  ChevronRight,
  Volume2,
  VolumeX,
  BellOff,
  BellRing,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import { formatNotificationTime } from "../../../dummyData";
import "../styles/NotificationMenu.css";
import { useNotifications } from "./NotificationContext";

const NotificationMenu = ({ notificationImage }) => {
  const [anchorEl, setAnchorEl] = React.useState(null);
  const navigate = useNavigate();

  const {
    notifications,
    unreadCount,
    markAsRead,
    markAllAsRead,
    isMuted,
    toggleMute,
    isPopupSilenced,
    togglePopupSilence,
  } = useNotifications();

  const open = Boolean(anchorEl);

  const handleClick = event => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleNotificationClick = notification => {
    // Mark as read
    markAsRead(notification.id);

    // Navigate to the linked page
    if (notification.link) {
      navigate(notification.link);
    }

    handleClose();
  };

  // Get icon based on notification type
  const getNotificationIcon = type => {
    switch (type) {
      case "order":
        return <ShoppingBag size={20} color="#5652B0" />;
      case "payment":
        return <CircleDollarSign size={20} color="#22c55e" />;
      case "inventory":
        return <Package size={20} color="#f59e0b" />;
      case "customer":
        return <Users size={20} color="#5652B0" />;
      case "request":
        return <FileQuestion size={20} color="#E96479" />;
      default:
        return <Bell size={20} color="#6B7280" />;
    }
  };

  const handleMuteToggle = event => {
    event.stopPropagation();
    event.preventDefault();
    toggleMute();
  };

  const handlePopupSilenceToggle = event => {
    event.stopPropagation();
    event.preventDefault();
    togglePopupSilence();
  };

  return (
    <>
      <Tooltip title="Notifications">
        <IconButton
          aria-label="notifications"
          onClick={handleClick}
          sx={{
            border: "1px solid #E5E7EB",
            width: "40px",
            height: "40px",
            borderRadius: "50%",
            "&:hover": {
              backgroundColor: "rgba(86, 82, 176, 0.04)",
              borderColor: "#5652B0",
            },
          }}>
          <Badge
            badgeContent={unreadCount}
            color="error"
            sx={{
              "& .MuiBadge-badge": {
                fontSize: "0.65rem",
                minWidth: "16px",
                height: "16px",
                top: 3,
                right: 3,
              },
            }}>
            {notificationImage ? (
              <img
                src={notificationImage}
                alt="notification"
                style={{ width: "20px", height: "20px" }}
              />
            ) : (
              <Bell size={20} color="#374151" />
            )}
          </Badge>
        </IconButton>
      </Tooltip>

      <Menu
        anchorEl={anchorEl}
        id="notification-menu"
        open={open}
        onClose={handleClose}
        PaperProps={{
          elevation: 2,
          sx: {
            width: "360px",
            maxHeight: "480px",
            overflow: "visible",
            mt: 1.5,
            border: "1px solid #E5E7EB",
            borderRadius: "12px",
            "&:before": {
              content: '""',
              display: "block",
              position: "absolute",
              top: 0,
              right: 20,
              width: 10,
              height: 10,
              bgcolor: "background.paper",
              transform: "translateY(-50%) rotate(45deg)",
              zIndex: 0,
              borderLeft: "1px solid #E5E7EB",
              borderTop: "1px solid #E5E7EB",
            },
          },
        }}
        transformOrigin={{ horizontal: "right", vertical: "top" }}
        anchorOrigin={{ horizontal: "right", vertical: "bottom" }}>
        <Box
          sx={{
            p: 2,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}>
          <Typography
            variant="subtitle1"
            className="outfit-semibold"
            sx={{ color: "#374151" }}>
            Notifications
          </Typography>
          <Box sx={{ display: "flex", alignItems: "center" }}>
            {unreadCount > 0 && (
              <Button
                onClick={e => {
                  e.stopPropagation();
                  markAllAsRead();
                }}
                size="small"
                sx={{
                  textTransform: "none",
                  color: "#5652B0",
                  fontSize: "0.75rem",
                  fontWeight: 500,
                  mr: 1,
                  "&:hover": {
                    bgcolor: "rgba(86, 82, 176, 0.04)",
                  },
                }}
                className="outfit-medium">
                Mark all as read
              </Button>
            )}

            <Tooltip
              title={isMuted ? "Unmute notifications" : "Mute notifications"}>
              <IconButton
                size="small"
                onClick={handleMuteToggle}
                sx={{
                  color: isMuted ? "#9CA3AF" : "#5652B0",
                  "&:hover": {
                    backgroundColor: "rgba(86, 82, 176, 0.04)",
                  },
                }}>
                {isMuted ? <VolumeX size={18} /> : <Volume2 size={18} />}
              </IconButton>
            </Tooltip>

            <Tooltip
              title={
                isPopupSilenced
                  ? "Show popup notifications"
                  : "Hide popup notifications"
              }>
              <IconButton
                size="small"
                onClick={handlePopupSilenceToggle}
                sx={{
                  color: isPopupSilenced ? "#9CA3AF" : "#5652B0",
                  "&:hover": {
                    backgroundColor: "rgba(86, 82, 176, 0.04)",
                  },
                  ml: 0.5,
                }}>
                {isPopupSilenced ? (
                  <BellOff size={18} />
                ) : (
                  <BellRing size={18} />
                )}
              </IconButton>
            </Tooltip>
          </Box>
        </Box>

        <Divider />

        {notifications.length === 0 ? (
          <Box
            sx={{
              py: 6,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              px: 2,
            }}>
            <Bell size={32} color="#9CA3AF" />
            <Typography
              sx={{ mt: 1, color: "#6B7280", fontSize: "0.875rem" }}
              className="outfit-medium">
              No notifications yet
            </Typography>
          </Box>
        ) : (
          <List sx={{ py: 0, maxHeight: "350px", overflowY: "auto" }}>
            {notifications.map(notification => (
              <React.Fragment key={notification.id}>
                <ListItem
                  alignItems="flex-start"
                  onClick={() => handleNotificationClick(notification)}
                  sx={{
                    py: 1.5,
                    cursor: "pointer",
                    bgcolor: notification.isRead
                      ? "transparent"
                      : "rgba(86, 82, 176, 0.04)",
                    transition: "all 0.2s ease",
                    "&:hover": {
                      bgcolor: "rgba(229, 231, 235, 0.5)",
                    },
                  }}>
                  <ListItemAvatar sx={{ minWidth: "44px" }}>
                    <Avatar
                      sx={{
                        bgcolor: notification.isRead
                          ? "rgba(229, 231, 235, 0.5)"
                          : "rgba(86, 82, 176, 0.1)",
                        width: 32,
                        height: 32,
                      }}>
                      {getNotificationIcon(notification.type)}
                    </Avatar>
                  </ListItemAvatar>
                  <ListItemText
                    primary={
                      <Typography
                        variant="body2"
                        className={
                          notification.isRead
                            ? "outfit-medium"
                            : "outfit-semibold"
                        }
                        sx={{
                          fontSize: "0.85rem",
                          color: "#374151",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                        }}>
                        <span>{notification.title}</span>
                        <Typography
                          component="span"
                          sx={{
                            fontSize: "0.7rem",
                            color: "#6B7280",
                            ml: 1,
                          }}>
                          {formatNotificationTime(notification.timestamp)}
                        </Typography>
                      </Typography>
                    }
                    secondary={
                      <Typography
                        variant="body2"
                        className="outfit-regular"
                        sx={{
                          fontSize: "0.75rem",
                          color: "#6B7280",
                          mt: 0.5,
                        }}>
                        {notification.message}
                      </Typography>
                    }
                  />
                </ListItem>
                <Divider component="li" sx={{ margin: 0 }} />
              </React.Fragment>
            ))}
          </List>
        )}

        <Box
          sx={{ p: 1.5, bgcolor: "#F9FAFB", borderTop: "1px solid #E5E7EB" }}>
          <Button
            fullWidth
            sx={{
              textTransform: "none",
              color: "#5652B0",
              justifyContent: "space-between",
              p: 1,
              borderRadius: "8px",
              "&:hover": {
                bgcolor: "rgba(86, 82, 176, 0.04)",
              },
            }}
            onClick={() => navigate("/notifications")}
            className="outfit-medium"
            endIcon={<ChevronRight size={16} />}>
            View all notifications
          </Button>
        </Box>
      </Menu>
    </>
  );
};

export default NotificationMenu;
